import { useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Instagram, ArrowLeft, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SendVideoPage() {
  const [mode, setMode] = useState("upload"); // 'upload' | 'instagram'
  const [form, setForm] = useState({
    artist_name: "",
    email: "",
    title: "",
    description: "",
    whatsapp: "",
    instagram: "",
    instagram_url: "",
  });
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!videoFile) {
      toast.error("Selecione um arquivo de vídeo (MP4, MOV)");
      return;
    }
    if (videoFile.size > 95 * 1024 * 1024) {
      toast.error("Vídeo muito grande. Máximo 95MB.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("artist_name", form.artist_name);
      fd.append("email", form.email);
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("whatsapp", form.whatsapp);
      fd.append("instagram", form.instagram);
      fd.append("video", videoFile);
      const r = await fetch(`${API}/api/video-submissions`, { method: "POST", body: fd });
      const d = await r.json();
      if (r.ok) {
        toast.success("✅ Vídeo enviado! Aguarde aprovação.");
        setForm({ artist_name: "", email: "", title: "", description: "", whatsapp: "", instagram: "", instagram_url: "" });
        setVideoFile(null);
      } else {
        toast.error(d.detail || "Erro no envio");
      }
    } catch {
      toast.error("Falha de rede");
    } finally {
      setSubmitting(false);
    }
  };

  const submitInstagram = async (e) => {
    e.preventDefault();
    if (!form.instagram_url || !form.instagram_url.includes("instagram.com")) {
      toast.error("Cole uma URL do Instagram (reel ou post)");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/video-submissions/from-instagram`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success("✅ Vídeo importado do Instagram! Aguarde aprovação.");
        setForm({ artist_name: "", email: "", title: "", description: "", whatsapp: "", instagram: "", instagram_url: "" });
      } else {
        toast.error(d.detail || "Erro ao importar");
      }
    } catch {
      toast.error("Falha de rede");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <Link
          to="/vitrine"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6"
        >
          <ArrowLeft size={16} /> Voltar para a Vitrine
        </Link>

        <h1 className="font-heading text-4xl sm:text-5xl font-black leading-tight mb-2">
          Envie seu <span className="text-[#FF007F] italic">Vídeo</span>
        </h1>
        <p className="text-zinc-500 mb-8">
          Mostre seu trabalho na Vitrine do Arrocha. Reels, shows, ensaios — tudo entra.
        </p>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 p-1 bg-white/5 rounded-full w-fit">
          <button
            onClick={() => setMode("upload")}
            className={`px-5 py-2 rounded-full font-bold text-sm transition flex items-center gap-2 ${
              mode === "upload" ? "bg-[#FF007F] text-white" : "text-zinc-400 hover:text-white"
            }`}
            data-testid="tab-upload"
          >
            <Upload size={16} /> Upload
          </button>
          <button
            onClick={() => setMode("instagram")}
            className={`px-5 py-2 rounded-full font-bold text-sm transition flex items-center gap-2 ${
              mode === "instagram" ? "bg-[#FF007F] text-white" : "text-zinc-400 hover:text-white"
            }`}
            data-testid="tab-instagram"
          >
            <Instagram size={16} /> Importar do Instagram
          </button>
        </div>

        <form
          onSubmit={mode === "upload" ? submitUpload : submitInstagram}
          className="space-y-4 bg-[#0F0F0F] border border-white/10 rounded-2xl p-6"
        >
          <div>
            <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
              Nome do artista *
            </label>
            <input
              required
              value={form.artist_name}
              onChange={(e) => onChange("artist_name", e.target.value)}
              className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
              placeholder="Ex: Wesley Safadão"
              data-testid="input-artist"
            />
          </div>

          <div>
            <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
              Email *
            </label>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => onChange("email", e.target.value)}
              className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
              placeholder="contato@exemplo.com"
              data-testid="input-email"
            />
          </div>

          <div>
            <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
              Título do vídeo *
            </label>
            <input
              required
              value={form.title}
              onChange={(e) => onChange("title", e.target.value)}
              className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
              placeholder="Ex: Show ao vivo em Salvador"
              data-testid="input-title"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
                WhatsApp
              </label>
              <input
                value={form.whatsapp}
                onChange={(e) => onChange("whatsapp", e.target.value)}
                className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
                placeholder="(85) 99999-9999"
              />
            </div>
            <div>
              <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
                Instagram
              </label>
              <input
                value={form.instagram}
                onChange={(e) => onChange("instagram", e.target.value)}
                className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
                placeholder="@artista"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
              Descrição (opcional)
            </label>
            <textarea
              value={form.description}
              onChange={(e) => onChange("description", e.target.value)}
              rows={3}
              className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none resize-none"
              placeholder="Conte sobre o vídeo, o show, a música..."
            />
          </div>

          {mode === "upload" ? (
            <div>
              <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
                Arquivo de vídeo (MP4 / MOV — máx 95MB) *
              </label>
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                className="w-full text-zinc-400 file:bg-[#FF007F] file:border-0 file:rounded-lg file:px-4 file:py-2 file:text-white file:font-bold file:cursor-pointer file:hover:bg-[#FF3399]"
                data-testid="input-video"
              />
              {videoFile && (
                <p className="text-xs text-zinc-500 mt-1">
                  {videoFile.name} — {(videoFile.size / 1024 / 1024).toFixed(1)} MB
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
                URL do Reel/Post do Instagram *
              </label>
              <input
                required
                type="url"
                value={form.instagram_url}
                onChange={(e) => onChange("instagram_url", e.target.value)}
                className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
                placeholder="https://www.instagram.com/reel/ABC123/"
                data-testid="input-instagram-url"
              />
              <p className="text-xs text-zinc-600 mt-1">
                Cole a URL do reel/post público. O sistema baixa o vídeo automaticamente.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-[#FF007F] to-[#FF3399] hover:from-[#FF3399] hover:to-[#FF007F] text-white font-bold py-4 rounded-full transition shadow-lg shadow-[#FF007F]/30 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="submit-video"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" size={18} /> Enviando...
              </>
            ) : (
              <>
                <Upload size={18} /> Enviar para aprovação
              </>
            )}
          </button>

          <p className="text-xs text-zinc-600 text-center pt-2">
            Seu vídeo será revisado pela equipe antes de aparecer na Vitrine.
          </p>
        </form>
      </main>
      <Footer />
    </div>
  );
}
